import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Badge,
  Tooltip,
  Box,
  Fade,
  Zoom
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationPopover from './NotificationModal';
import { useLoginApi } from '../../hooks/useLoginApi';
import {COLORS} from "../../theme/themeConstants"
// Helper to read count from localStorage
const getInitialNotificationCount = (): number => {
  try {
    const stored = localStorage.getItem('userData');
    if (!stored) return 0;

    const parsed = JSON.parse(stored);
    const count = parsed?.userDetails?.notificationsCount || 0;

    return count;
  } catch (err) {
    console.error('Error parsing notificationsCount:', err);
    return 0;
  }
};

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState<number>(getInitialNotificationCount());
  const { getSpecialUser } = useLoginApi();

  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setUnreadCount(0);
    // Update localStorage
    try {
      const stored = localStorage.getItem('userData');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.userDetails) {
          parsed.userDetails.notificationsCount = 0;
          localStorage.setItem('userData', JSON.stringify(parsed));
        }
      }
    } catch (error) {
      console.error('Failed to update notification count in localStorage:', error);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Polling for unread notification count every 1 minute using getSpecialUser
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const fetchCount = async () => {
      try {
        const stored = localStorage.getItem('userData');
        if (!stored) return;
        const parsed = JSON.parse(stored);
        const userId = parsed?.userId;
        const userType = parsed?.userType;
        if (!userId || !userType) return;
        const res = await getSpecialUser({ userId, userType });
        if (res && typeof res === 'object' && 'notificationsCount' in res) {
          const count = (res as { notificationsCount?: unknown }).notificationsCount;
          if (typeof count === 'number') {
            setUnreadCount(count);
          }
        }
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchCount(); // Initial fetch
    interval = setInterval(fetchCount, 60000); // 1 minute
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [getSpecialUser]);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={handleBellClick}
          sx={{
            position: 'relative',
            width: '37px',          // Increase width
            height: '37px',
            borderRadius: '50%',
            backgroundColor: COLORS.primary.main,
            padding: '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: COLORS.primary.main,
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
        >
          <Fade in={true} timeout={300}>
            <Box sx={{ position: 'relative' }}>
              <NotificationsIcon
                sx={{
                  color: COLORS.secondary.main,
                  fontSize: '24px'
                }}
              />
              {unreadCount > 0 && (
                <Zoom in={true} timeout={200}>
                  <Badge
                    badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      '& .MuiBadge-badge': {
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                          },
                        },
                      }
                    }}
                  />
                </Zoom>
              )}
            </Box>
          </Fade>
        </IconButton>
      </Tooltip>
      {Boolean(anchorEl) && (
        <NotificationPopover
          anchorEl={anchorEl}
          open={true}
          onClose={handlePopoverClose}
        />
      )}
    </>
  );
};

export default NotificationBell;
